"use client";

import React, { Suspense, useEffect, useId, useRef, useState, useCallback } from "react";
import Link from "next/link";
import WalletButtons from './WalletButtons';
import { useSearchParams } from "next/navigation";
import { useEvmWallet, type Eip1193Provider } from "../../components/EvmWalletProvider";
import { hexlify, toUtf8Bytes } from "ethers";

// Raw shape of an injected provider before we've confirmed it implements EIP-1193.
type RawInjectedProvider = {
  request?: unknown;
  providers?: unknown[];
  isMetaMask?: boolean;
};

function isEip1193Provider(p: unknown): p is Eip1193Provider {
  return !!p && typeof p === 'object' && typeof (p as Record<string, unknown>).request === 'function';
}

function getErrorMessage(e: unknown): string {
  if (e && typeof e === 'object' && typeof (e as { message?: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return '';
}

function getErrorName(e: unknown): string | undefined {
  if (e && typeof e === 'object' && typeof (e as { name?: unknown }).name === 'string') {
    return (e as { name: string }).name;
  }
  return undefined;
}

function getErrorCode(e: unknown): number | string | undefined {
  if (e && typeof e === 'object' && 'code' in e) {
    return (e as { code?: number | string }).code;
  }
  return undefined;
}

function withTimeout<T>(p: Promise<T>, ms: number, timeoutMessage: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error(timeoutMessage)), ms);
    }),
  ]);
}

// ── Small inline icon set (decorative only — kept local since these are
// only used on this screen) ─────────────────────────────────────────────
function IconShieldMark({ size = 22 }: { size?: number }) {
  // Each render gets its own gradient id (via useId) since this component is
  // mounted twice at once (desktop brand panel + mobile header logo) — two
  // <svg> elements sharing one hardcoded id would be invalid, duplicate DOM
  // ids, which some browsers render inconsistently for gradient fills.
  const gradId = `authShieldGrad-${useId()}`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 2.5L20.5 6.5V12.2C20.5 16.9 17.2 21.2 12 22.5C6.8 21.2 3.5 16.9 3.5 12.2V6.5L12 2.5Z" stroke={`url(#${gradId})`} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M8.3 12.1L10.8 14.6L15.9 9.4" stroke={`url(#${gradId})`} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id={gradId} x1="3.5" y1="2.5" x2="20.5" y2="22.5" gradientUnits="userSpaceOnUse">
          <stop stopColor="var(--brand-cyan)" />
          <stop offset="1" stopColor="var(--brand-violet)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function IconLock({ className }: { className?: string }) {
  return (
    <svg className={className} width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 11V7.5C8 5 9.8 3 12 3C14.2 3 16 5 16 7.5V11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IconInfo({ className }: { className?: string }) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 11v5.5M12 8v.01" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function IconRadar({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="8.6" stroke="currentColor" strokeWidth="1.4" opacity="0.45" />
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="1.4" opacity="0.75" />
      <circle cx="12" cy="12" r="1.7" fill="currentColor" />
    </svg>
  );
}

function IconShieldCheck({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12 3L19 6V11.5C19 16 15.9 19.9 12 21C8.1 19.9 5 16 5 11.5V6L12 3Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8.8 12L10.9 14.1L15.3 9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconHandshake({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M3 11L7.5 6.5L11 9.5L13 7.5L18 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 7.5L21 7.5V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 11V16.5H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 16.5L12.5 20L18.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BrandNetworkGraphic() {
  return (
    <svg viewBox="0 0 600 800" className="h-full w-full" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g stroke="url(#netGrad)" strokeWidth="1" opacity="0.5">
        <line x1="90" y1="130" x2="230" y2="230" />
        <line x1="230" y1="230" x2="130" y2="365" />
        <line x1="230" y1="230" x2="385" y2="185" />
        <line x1="385" y1="185" x2="500" y2="305" />
        <line x1="130" y1="365" x2="265" y2="460" />
        <line x1="265" y1="460" x2="420" y2="425" />
        <line x1="420" y1="425" x2="500" y2="305" />
        <line x1="265" y1="460" x2="205" y2="615" />
        <line x1="420" y1="425" x2="475" y2="600" />
        <line x1="205" y1="615" x2="330" y2="690" />
        <line x1="475" y1="600" x2="330" y2="690" />
      </g>
      <g fill="url(#nodeGrad)">
        <circle cx="90" cy="130" r="4" />
        <circle cx="230" cy="230" r="5.5" />
        <circle cx="385" cy="185" r="4" />
        <circle cx="500" cy="305" r="4.5" />
        <circle cx="130" cy="365" r="4" />
        <circle cx="265" cy="460" r="6" />
        <circle cx="420" cy="425" r="4" />
        <circle cx="205" cy="615" r="4" />
        <circle cx="475" cy="600" r="4" />
        <circle cx="330" cy="690" r="5" />
      </g>
      <defs>
        <linearGradient id="netGrad" x1="0" y1="0" x2="600" y2="800" gradientUnits="userSpaceOnUse">
          <stop stopColor="#00F5FF" />
          <stop offset="1" stopColor="#7A35FF" />
        </linearGradient>
        <radialGradient id="nodeGrad">
          <stop stopColor="#9FF8FF" />
          <stop offset="1" stopColor="#00D4FF" />
        </radialGradient>
      </defs>
    </svg>
  );
}

const BRAND_FEATURES = [
  {
    Icon: IconRadar,
    title: 'Wallet risk scoring',
    shortTitle: 'Risk scoring',
    detail: 'Instant trust signals across EVM chains, before you sign anything.',
  },
  {
    Icon: IconShieldCheck,
    title: 'Scam & phishing detection',
    shortTitle: 'Scam detection',
    detail: 'Real-time alerts flag malicious contracts and drainer addresses.',
  },
  {
    Icon: IconHandshake,
    title: 'Charity & fundraiser verification',
    shortTitle: 'Verified charities',
    detail: 'Give and fundraise with on-chain accountability, not blind trust.',
  },
];

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="auth-shell flex min-h-screen items-center justify-center px-4">
          <div className="auth-card w-full max-w-[420px] rounded-[28px] border p-8">
            <div className="text-center">
              <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-[color:var(--brand-cyan)] border-t-transparent" />
              <h1 className="text-2xl font-bold text-[color:var(--brand-text)]">Loading…</h1>
              <p className="auth-subcopy mt-2 text-sm">Preparing wallet sign-in</p>
            </div>
          </div>
        </div>
      }
    >
      <LoginPageInner />
    </Suspense>
  );
}

function LoginPageInner() {

  const searchParams = useSearchParams();
  const [mounted, setMounted] = useState(false);
  const { address, isConnected, disconnect, getSigner, provider } = useEvmWallet();
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [prefetchedNonce, setPrefetchedNonce] = useState<string | null>(null);
  const [prefetchedAt, setPrefetchedAt] = useState<number>(0);
  const [nonceWarm, setNonceWarm] = useState(false);
  const [stage, setStage] = useState<'idle' | 'nonce' | 'sign' | 'verify'>('idle');

  const redirectHint = searchParams?.get('redirectTo') || '';
  const stepItems = [
    {
      title: 'Connect wallet',
      detail: 'Choose a wallet app or extension to start.',
      active: !isConnected,
      complete: isConnected,
    },
    {
      title: 'Approve signature',
      detail: 'Sign the secure gasless message in your wallet.',
      active: isConnected && (stage === 'nonce' || stage === 'sign'),
      complete: stage === 'verify',
    },
    {
      title: 'Enter workspace',
      detail: 'We verify ownership and continue to your destination.',
      active: stage === 'verify',
      complete: false,
    },
  ];



  function getBestEip1193Provider(): Eip1193Provider | null {
    if (provider && typeof provider.request === 'function') return provider;
    if (typeof window === 'undefined') return null;
    const eth = (window as unknown as { ethereum?: RawInjectedProvider }).ethereum;
    if (!eth) return null;
    // Prefer MetaMask if multiple providers are present.
    if (Array.isArray(eth.providers)) {
      const mm = eth.providers.find((p) => !!p && typeof p === 'object' && (p as RawInjectedProvider).isMetaMask);
      if (isEip1193Provider(mm)) return mm;
    }
    return isEip1193Provider(eth) ? eth : null;
  }

  const fetchNonce = useCallback(async (opts: { address: string; signal?: AbortSignal }): Promise<string> => {
    const { address, signal } = opts;
    const nonceRes = await withTimeout(
      fetch("/api/auth/nonce", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        cache: "no-store",
        body: JSON.stringify({ address }),
        signal,
      }),
      60_000,
      'Nonce request timed out. Please retry.'
    );
    const nonceJson = await nonceRes.json().catch(() => ({} as { message?: string; nonce?: string }));
    if (!nonceRes.ok) {
      throw new Error(nonceJson?.message || nonceRes.statusText || "Failed to fetch nonce");
    }
    const nonce = String(nonceJson?.nonce || '');
    if (!nonce) throw new Error('Nonce missing from server response');
    return nonce;
  }, []);

  function prettyWalletError(e: unknown): string {
    const code = getErrorCode(e);
    const msg = getErrorMessage(e) || String(e ?? '');
    // MetaMask: -32002 means a request (connect/sign) is already pending.
    if (code === -32002 || msg.toLowerCase().includes('already pending')) {
      return 'A wallet request is already pending. Open your wallet and approve or reject the existing request, then retry.';
    }
    if (code === 4001 || msg.toLowerCase().includes('user rejected')) {
      return 'Signature was rejected in your wallet. Please retry and approve the signature.';
    }
    return msg || 'Wallet error. Please retry.';
  }

  async function ensureWalletAccountsReady(p: Eip1193Provider | null, expectedAddress: string) {
    if (!p || typeof p.request !== 'function') return;

    // Some wallet providers can appear “connected” in app state but still require
    // a permissions prompt before signing. This forces the prompt up-front.
    let accounts: string[] = [];
    try {
      const res = await withTimeout(
        Promise.resolve(p.request({ method: 'eth_accounts' })),
        8_000,
        'Wallet is not responding. Make sure your wallet extension/app is unlocked.'
      );
      if (Array.isArray(res)) accounts = res.map((a) => String(a));
    } catch (e) {
      // Ignore and try requestAccounts.
      console.warn('LOGIN: eth_accounts failed', e);
    }

    const exp = String(expectedAddress || '').toLowerCase();
    const hasExpected = accounts.some((a) => String(a).toLowerCase() === exp);
    if (accounts.length === 0 || (exp && !hasExpected)) {
      try {
        await withTimeout(
          Promise.resolve(p.request({ method: 'eth_requestAccounts' })),
          25_000,
          'Wallet connection request timed out. Open your wallet and approve the connection.'
        );
      } catch (e) {
        throw new Error(prettyWalletError(e));
      }
    }
  }

  async function signLoginMessage(opts: { message: string; address: string }): Promise<string> {
    const { message, address } = opts;

    const eip1193 = getBestEip1193Provider();
    if (eip1193) {
      await ensureWalletAccountsReady(eip1193, address);
    }

    // Prefer direct EIP-1193 signing first. In some environments, ethers'
    // Signer.signMessage can hang even though the raw request works.
    if (eip1193 && typeof eip1193.request === 'function') {
      const req = eip1193.request.bind(eip1193);
      const addr = String(address || '').toLowerCase();

      // Try plain string first (widest compatibility).
      try {
        const sig = await withTimeout(
          Promise.resolve(req({ method: 'personal_sign', params: [message, addr] })),
          90_000,
          'Signature request timed out. Open your wallet and approve the signature.'
        );
        if (typeof sig === 'string' && sig) return sig;
      } catch (e) {
        const msg = prettyWalletError(e);
        // If it's a hard user-facing error (reject/pending), surface it.
        if (msg && msg !== getErrorMessage(e)) throw new Error(msg);
      }

      // Then try hex-encoded message (some providers prefer this).
      const msgHex = hexlify(toUtf8Bytes(message));
      try {
        const sig = await withTimeout(
          Promise.resolve(req({ method: 'personal_sign', params: [msgHex, addr] })),
          90_000,
          'Signature request timed out. Open your wallet and approve the signature.'
        );
        if (typeof sig === 'string' && sig) return sig;
      } catch {
        // Some wallets flip param order: [address, data]
        const sig = await withTimeout(
          Promise.resolve(req({ method: 'personal_sign', params: [addr, msgHex] })),
          90_000,
          'Signature request timed out. Open your wallet and approve the signature.'
        );
        if (typeof sig === 'string' && sig) return sig;
      }
    }

    // 1) Try ethers Signer (preferred when it works)
    const signer = await withTimeout(
      getSigner(),
      12_000,
      'Wallet signer not ready. Try disconnecting and reconnecting your wallet.'
    );

    try {
      return await withTimeout(
        signer.signMessage(message),
        90_000,
        'Signature request timed out. Open your wallet and approve the signature.'
      );
    } catch (e) {
      throw new Error(prettyWalletError(e) || 'Signature failed. Please retry and approve the signature in your wallet.');
    }
  }

  useEffect(() => {
    setMounted(true);
  }, []);

  // Warm up the nonce route as soon as the wallet is connected.
  // This avoids a long await (dev compile/cold route) between the click and the
  // signature request, which can prevent some wallets from surfacing the prompt.
  useEffect(() => {
    if (!mounted) return;
    if (!isConnected || !address) {
      setPrefetchedNonce(null);
      setPrefetchedAt(0);
      setNonceWarm(false);
      return;
    }

    // Only prefetch if we don't have a fresh nonce (nonce cookie is 5m).
    const ageMs = Date.now() - (prefetchedAt || 0);
    if (prefetchedNonce && ageMs < 4 * 60 * 1000) {
      setNonceWarm(true);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setNonceWarm(false);

    (async () => {
      try {
        const nonce = await fetchNonce({ address, signal: controller.signal });
        if (cancelled) return;
        setPrefetchedNonce(nonce);
        setPrefetchedAt(Date.now());
        setNonceWarm(true);
      } catch (e) {
        if (cancelled) return;
        // Non-fatal; we'll fetch on click.
        console.warn('LOGIN: nonce prefetch failed', e);
      }
    })();

    return () => {
      cancelled = true;
      try {
        controller.abort();
      } catch {
        // ignore
      }
    };
  }, [address, isConnected, mounted, prefetchedAt, prefetchedNonce, fetchNonce]);

  async function handleLogin() {
    // React state updates are async; use a ref guard to prevent double clicks from
    // triggering multiple concurrent signature requests.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (loading) {
      inFlightRef.current = false;
      return;
    }
    setError("");
    setLoading(true);
    setStage('nonce');

    // Cancel any previous in-flight attempt before starting a new one.
    try {
      abortRef.current?.abort();
    } catch {
      // ignore
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const isRetryableNonceError = (message: unknown) => {
      const m = String(message || '').toLowerCase();
      return m.includes('nonce expired') || m.includes('missing login nonce');
    };

    const attemptVerify = async (nonceToUse: string) => {
      setStage('sign');
      const message = `Login nonce: ${nonceToUse}`;

      // 2. Sign nonce with the currently connected wallet
      const signature = await signLoginMessage({ message, address: String(address) });
      console.log('LOGIN: obtained signature', { signature });

      setStage('verify');
      const verifyRes = await withTimeout(
        fetch("/api/auth/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          cache: "no-store",
          body: JSON.stringify({ address, signature }),
          signal: controller.signal,
        }),
        60_000,
        'Verify request timed out. Please retry.'
      );
      const verifyJson = await verifyRes.json().catch(() => ({} as { message?: string }));
      console.log('LOGIN: verify response', verifyRes.status, verifyJson);
      return { ok: verifyRes.ok, status: verifyRes.status, json: verifyJson };
    };

    try {
      if (!address) throw new Error('Connect a wallet first.');
      console.log('LOGIN: starting handleLogin', { address });

      // 1) Use prefetched nonce if fresh; otherwise fetch now.
      let nonceToUse: string | null = null;
      const ageMs = Date.now() - (prefetchedAt || 0);
      if (prefetchedNonce && ageMs < 4 * 60 * 1000) {
        nonceToUse = prefetchedNonce;
        console.log('LOGIN: using prefetched nonce');
      } else {
        const fetched = await fetchNonce({ address, signal: controller.signal });
        nonceToUse = fetched;
        setPrefetchedNonce(fetched);
        setPrefetchedAt(Date.now());
        console.log('LOGIN: received nonce', { nonce: fetched });
      }

      let verifyAttempt = await attemptVerify(String(nonceToUse));
      if (!verifyAttempt.ok && verifyAttempt.status === 400 && isRetryableNonceError(verifyAttempt.json?.message)) {
        console.warn('LOGIN: nonce invalid/expired; fetching fresh nonce and retrying once');
        const fresh = await fetchNonce({ address, signal: controller.signal });
        setPrefetchedNonce(fresh);
        setPrefetchedAt(Date.now());
        setNonceWarm(true);
        verifyAttempt = await attemptVerify(fresh);
      }
      if (!verifyAttempt.ok) {
        throw new Error(verifyAttempt.json?.message || "Verification failed");
      }

      // Save wallet address for admin panel access
      window.localStorage.setItem("wallet", address || "");
      // Notify the app shell (Navbar, etc.) that auth state changed
      try {
        window.dispatchEvent(new Event('auth-changed'));
      } catch {
        // ignore
      }
      // On success, land on /profile first (required), but preserve intent.
      // FIX-50: brand-new accounts (no User doc existed before this login -
      // see isNewUser in app/api/auth/verify/route.ts) land on /welcome
      // instead, which forwards the same redirectTo on to /profile once the
      // user clicks through.
      // Strip any redirectTo already embedded in this value before re-wrapping it,
      // so a prior bounce through /login can't nest redirectTo inside itself again.
      const isNewUser = Boolean(verifyAttempt.json?.isNewUser);
      const basePath = isNewUser ? '/welcome' : '/profile';
      const redirectTo = searchParams?.get('redirectTo') || '';
      let target = basePath;
      if (redirectTo) {
        const [rPath, rQs] = redirectTo.split('?');
        const sp = new URLSearchParams(rQs || '');
        sp.delete('redirectTo');
        const cleanQs = sp.toString();
        const cleanRedirect = cleanQs ? `${rPath}?${cleanQs}` : rPath;
        target = `${basePath}?redirectTo=${encodeURIComponent(cleanRedirect)}`;
      }
      // Use a hard navigation so the newly set HttpOnly cookie is definitely
      // observed by middleware and any RSC fetches on the destination route.
      window.location.assign(target);
      return;
    } catch (err) {
      // If the user clicked Disconnect or otherwise cancelled, do not show an error.
      const errMsg = getErrorMessage(err);
      const isAbort =
        getErrorName(err) === 'AbortError' ||
        errMsg.toLowerCase().includes('aborted') ||
        errMsg.toLowerCase().includes('canceled') ||
        errMsg.toLowerCase().includes('cancelled');
      if (isAbort) {
        console.log('LOGIN: aborted');
        return;
      }

      // Log the actual error (not the handler arguments) so we can diagnose failures
      try {
        console.error('LOGIN: sign-in flow failed', err);
      } catch {
        console.error('LOGIN: sign-in flow failed (unable to stringify error)');
      }
      const msg = errMsg || 'Login failed. Check console/network for details.';
      setError(String(msg));
    } finally {
      setLoading(false);
      setStage('idle');
      inFlightRef.current = false;
      // Clear controller if this attempt is the active one.
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  // Abort any in-flight verification if the user navigates away.
  useEffect(() => {
    return () => {
      try {
        abortRef.current?.abort();
      } catch {
        // ignore
      }
    };
  }, []);

  const eipProvider = getBestEip1193Provider();
  const canAttemptSignIn = Boolean(address && isConnected && eipProvider);

  // Mobile deep-link flow: if we previously sent the user into a wallet app,
  // auto-continue sign-in once they return with a connected provider.
  useEffect(() => {
    if (!mounted) return;
    if (!canAttemptSignIn) return;
    if (inFlightRef.current || loading) return;
    try {
      const pending = window.localStorage.getItem('walletLoginPending');
      if (pending !== 'true') return;
      window.localStorage.removeItem('walletLoginPending');
    } catch {
      return;
    }

    // Defer a tick so the UI can settle.
    const t = setTimeout(() => {
      handleLogin();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, canAttemptSignIn]);

  if (!mounted) {
    return (
      <div className="auth-shell flex min-h-screen items-center justify-center px-4">
        <div className="auth-card w-full max-w-[420px] rounded-[28px] border p-8">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-[color:var(--brand-cyan)] border-t-transparent" />
            <h1 className="text-2xl font-bold text-[color:var(--brand-text)]">Loading…</h1>
            <p className="auth-subcopy mt-2 text-sm">Preparing wallet sign-in</p>
          </div>
        </div>
      </div>
    );
  }
  const stageText =
    stage === 'nonce'
      ? 'Preparing sign-in…'
      : stage === 'sign'
        ? 'Waiting for signature in your wallet…'
        : stage === 'verify'
          ? 'Verifying signature…'
          : nonceWarm
            ? 'Ready to sign.'
            : isConnected
              ? 'Wallet connected — ready to sign in.'
              : 'Connect your wallet to continue.';

  const primaryButtonText =
    stage === 'nonce'
      ? 'Preparing…'
      : stage === 'sign'
        ? 'Awaiting Signature…'
        : stage === 'verify'
          ? 'Verifying…'
          : 'Sign In';

  return (
    <div className="auth-shell min-h-screen">
      <div className="mx-auto grid min-h-screen w-full max-w-[1440px] lg:grid-cols-[minmax(0,1.05fr)_minmax(0,560px)]">
        {/* Brand panel — desktop only. Sets the premium, product-led tone
            without competing for attention with the auth card itself. */}
        <aside className="auth-brand-panel relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:px-14 lg:py-14 xl:px-20">
          <div className="auth-brand-graphic absolute inset-0" aria-hidden="true">
            <BrandNetworkGraphic />
          </div>

          <div className="relative z-10 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[color:var(--brand-stroke-strong)] bg-[image:var(--brand-surface)]">
              <IconShieldMark size={20} />
            </div>
            <span className="text-lg font-bold tracking-tight text-[color:var(--brand-text)]">Reqrium</span>
          </div>

          <div className="relative z-10 max-w-md">
            <p className="auth-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em]">Trust intelligence for Web3</p>
            <h1 className="mt-3 text-[2.4rem] font-extrabold leading-[1.08] tracking-tight text-[color:var(--brand-text)] xl:text-[2.75rem]">
              Real-time trust,<br />verified on-chain.
            </h1>
            <p className="auth-subcopy mt-4 text-[15px] leading-relaxed">
              Reqrium screens wallets, contracts, and fundraisers so you can act with confidence — before you ever sign a transaction.
            </p>

            <div className="mt-10 space-y-5">
              {BRAND_FEATURES.map(({ Icon, title, detail }) => (
                <div key={title} className="flex items-start gap-3.5">
                  <span className="auth-feature-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border">
                    <Icon />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-[color:var(--brand-text)]">{title}</div>
                    <div className="auth-subcopy mt-0.5 text-[13px] leading-relaxed">{detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="auth-subcopy relative z-10 text-xs">
            Built for wallets, charities, and on-chain communities that value trust.
          </p>
        </aside>

        {/* Auth card panel */}
        <main
          className="relative flex flex-1 flex-col items-center justify-center px-5 py-10 sm:px-8 sm:py-12"
          style={{ paddingBottom: 'max(2.5rem, calc(env(safe-area-inset-bottom) + 1.5rem))' }}
        >
          {/* Mobile-only hero — the desktop brand panel is hidden below `lg`,
              so without this, phone visitors would land straight on a bare
              card with zero product context. This gives them the same
              trust signals in a compact, native-feeling strip. */}
          <div className="auth-mobile-hero mb-7 flex w-full max-w-[420px] flex-col items-center text-center sm:mb-8 lg:hidden">
            <div className="auth-mobile-glow" aria-hidden="true" />
            <Link href="/" className="relative z-10 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-[color:var(--brand-stroke-strong)] bg-[image:var(--brand-surface)]">
                <IconShieldMark size={18} />
              </div>
              <span className="text-base font-bold tracking-tight text-[color:var(--brand-text)]">Reqrium</span>
            </Link>
            <p className="auth-subcopy relative z-10 mt-2.5 max-w-[280px] text-[13px] leading-relaxed">
              Real-time trust intelligence for Web3 — screened wallets, verified charities, zero guesswork.
            </p>
            <div className="relative z-10 mt-4 flex w-full items-stretch justify-center gap-2">
              {BRAND_FEATURES.map(({ Icon, title, shortTitle }) => (
                <div key={title} className="auth-trust-chip flex flex-1 flex-col items-center gap-1.5 rounded-2xl border px-2 py-3">
                  <Icon />
                  <span className="text-[10.5px] font-semibold leading-tight">{shortTitle}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="auth-card w-full max-w-[420px] rounded-[28px] border p-6 sm:p-9">
            <div className="mb-7">
              <p className="auth-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em]">Secure sign-in</p>
              <h2 className="mt-2 text-[1.7rem] font-extrabold leading-tight tracking-tight text-[color:var(--brand-text)] sm:text-3xl">
                {isConnected ? 'Confirm it’s you' : 'Sign in to Reqrium'}
              </h2>
              <p className="auth-subcopy mt-2 text-sm leading-relaxed">
                {isConnected
                  ? 'Approve one gasless signature to verify wallet ownership.'
                  : 'Connect a wallet to continue — no password required.'}
              </p>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-1.5">
                {stepItems.map((step) => (
                  <span
                    key={step.title}
                    className={`h-[3px] flex-1 rounded-full transition-colors duration-300 ${
                      step.complete ? 'bg-[color:var(--brand-cyan)]' : step.active ? 'bg-[color:var(--brand-cyan)]/50' : 'bg-white/10'
                    }`}
                  />
                ))}
              </div>
              <p className="auth-stage-text mt-2.5 text-xs font-medium">{stageText}</p>
            </div>

            {redirectHint ? (
              <div className="auth-hint mb-6 flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-xs leading-relaxed">
                <IconInfo className="mt-[1px] shrink-0" />
                <span>
                  After sign-in, you&apos;ll continue to <span className="font-semibold" style={{ color: 'var(--brand-text, #eaf0ff)' }}>{redirectHint}</span>.
                </span>
              </div>
            ) : null}

            {!isConnected ? (
              <WalletButtons onError={(e: unknown) => {
                const obj = e as Record<string, unknown>;
                const msg = typeof e === 'string' ? e : (obj && typeof obj.message === 'string') ? String(obj.message) : String(e);
                setError(msg);
              }} />
            ) : (
              <div className="space-y-4">
                <div className="auth-address-chip flex items-center gap-3 rounded-2xl border px-4 py-3">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  <span className="min-w-0 flex-1 truncate font-mono text-[13px]" style={{ color: 'var(--brand-text, #eaf0ff)' }}>{address}</span>
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-emerald-300">Connected</span>
                </div>

                <button
                  className="w-full btn-primary py-3.5 text-[15px] font-bold transition-transform duration-200 hover:scale-[1.015] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100 disabled:active:scale-100"
                  onClick={handleLogin}
                  disabled={loading || !canAttemptSignIn}
                >
                  {primaryButtonText}
                </button>

                {!loading && !canAttemptSignIn && (
                  <div className="auth-alert auth-alert-warning rounded-xl border px-3.5 py-2.5 text-center text-xs">
                    Wallet is still initializing. If this persists, disconnect and reconnect, or reopen the page inside your wallet app on mobile.
                  </div>
                )}

                <button
                  className="w-full text-sm text-slate-300 transition-colors hover:text-[color:var(--brand-text)]"
                  onClick={async () => {
                    try {
                      try {
                        abortRef.current?.abort();
                        abortRef.current = null;
                      } catch {
                        // ignore
                      }
                      setLoading(false);
                      setError("");
                      await disconnect();
                      window.localStorage.removeItem('wallet');
                    } catch (e) {
                      console.error('DISCONNECT: failed', e);
                      setError(getErrorMessage(e) || 'Failed to disconnect');
                    }
                  }}
                >
                  Disconnect
                </button>
              </div>
            )}

            {error && (
              <div className="auth-alert auth-alert-error mt-5 rounded-xl border px-3.5 py-2.5 text-center text-sm">
                {error}
              </div>
            )}
          </div>

          <p className="auth-subcopy mt-7 flex max-w-[420px] items-center justify-center gap-1.5 text-center text-xs">
            <IconLock className="shrink-0" />
            We never ask for your seed phrase. Signatures are gasless and only prove wallet ownership.
          </p>
        </main>
      </div>

      <style jsx>{`
        .auth-shell {
          position: relative;
          isolation: isolate;
          background: var(--brand-bg, #000010);
          font-family: var(--font-body), "Segoe UI", sans-serif;
        }

        .auth-shell::before {
          content: "";
          position: fixed;
          inset: 0;
          z-index: -1;
          pointer-events: none;
          background:
            radial-gradient(circle at 12% 8%, rgba(0, 245, 255, 0.12), transparent 32%),
            radial-gradient(circle at 90% 4%, rgba(122, 53, 255, 0.14), transparent 30%),
            radial-gradient(circle at 55% 100%, rgba(230, 92, 255, 0.07), transparent 42%),
            linear-gradient(180deg, var(--brand-bg-soft, #020a1e) 0%, var(--brand-bg, #000010) 60%);
        }

        .auth-brand-panel {
          border-right: 1px solid var(--brand-stroke, rgba(148, 163, 184, 0.14));
          background: linear-gradient(180deg, rgba(6, 14, 32, 0.6), rgba(2, 6, 18, 0.85));
        }

        .auth-brand-graphic {
          opacity: 0.55;
          mask-image: radial-gradient(ellipse 90% 70% at 30% 40%, black 45%, transparent 85%);
          -webkit-mask-image: radial-gradient(ellipse 90% 70% at 30% 40%, black 45%, transparent 85%);
        }

        .auth-feature-icon {
          color: var(--brand-cyan, #00f5ff);
          border-color: var(--brand-stroke-strong, rgba(0, 245, 255, 0.22));
          background: rgba(0, 245, 255, 0.06);
        }

        .auth-card {
          position: relative;
          z-index: 1;
          border-color: var(--brand-stroke-strong, rgba(0, 245, 255, 0.22));
          background: var(--brand-panel, #06122d);
          background-image: linear-gradient(180deg, rgba(14, 26, 52, 0.98), rgba(6, 12, 26, 1));
          box-shadow: var(--brand-panel-shadow, 0 24px 70px -36px rgba(0, 0, 0, 0.9));
        }

        .auth-eyebrow {
          color: #7fe3ff;
        }

        .auth-subcopy {
          color: #c3cbe0;
        }

        .auth-stage-text {
          color: #d7e3f5;
        }

        .auth-hint {
          border-color: rgba(0, 245, 255, 0.22);
          background: rgba(0, 245, 255, 0.07);
          color: #d7f4ff;
        }

        .auth-address-chip {
          border-color: rgba(52, 211, 153, 0.32);
          background: rgba(16, 185, 129, 0.1);
        }

        .auth-alert {
          font-weight: 500;
        }

        .auth-alert-warning {
          border-color: rgba(251, 191, 36, 0.35);
          background: rgba(251, 191, 36, 0.1);
          color: #ffe4a8;
        }

        .auth-alert-error {
          border-color: rgba(248, 113, 113, 0.35);
          background: rgba(248, 113, 113, 0.1);
          color: #ffd0d0;
        }

        /* ── Mobile hero + entrance polish ──────────────────────────────── */
        .auth-mobile-hero {
          position: relative;
        }

        .auth-mobile-glow {
          position: absolute;
          top: -40px;
          left: 50%;
          width: 220px;
          height: 160px;
          transform: translateX(-50%);
          pointer-events: none;
          z-index: 0;
          background: radial-gradient(ellipse at center, rgba(0, 245, 255, 0.16), transparent 70%);
          filter: blur(6px);
        }

        .auth-trust-chip {
          color: var(--brand-cyan, #00f5ff);
          border-color: rgba(148, 163, 184, 0.16);
          background: rgba(255, 255, 255, 0.025);
        }

        .auth-trust-chip span {
          color: #c3cbe0;
        }

        .auth-mobile-hero,
        .auth-card {
          animation: auth-rise 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .auth-card {
          animation-delay: 0.06s;
        }

        @keyframes auth-rise {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .auth-mobile-hero,
          .auth-card {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
